/**
 * HowItWorksPanel — collapsible "How it works" card for explaining a feature
 * to users with concrete examples.
 *
 * Closed by default; expand/collapse state persists per `storageKey` so power
 * users keep it hidden across sessions.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, HelpCircle } from 'lucide-react';

import { cn } from '@/lib/utils';

export interface HowItWorksPanelProps {
  title: string;
  storageKey: string;
  children: ReactNode;
  className?: string;
}

function readPersistedOpen(storageKey: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(storageKey) === '1';
  } catch {
    return false;
  }
}

const HowItWorksPanel: FC<HowItWorksPanelProps> = ({
  title,
  storageKey,
  children,
  className,
}) => {
  const initialOpen = useMemo(() => readPersistedOpen(storageKey), [storageKey]);
  const [open, setOpen] = useState<boolean>(initialOpen);

  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, open ? '1' : '0');
    } catch {
      // ignore storage errors
    }
  }, [storageKey, open]);

  return (
    <div
      className={cn(
        'rounded-md border border-border bg-muted/30',
        open ? 'pb-2' : '',
        className,
      )}
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs font-medium text-muted-foreground transition-colors hover:text-foreground',
        )}
      >
        <HelpCircle className="h-3.5 w-3.5 shrink-0 text-primary/80" aria-hidden />
        <span>{title}</span>
        <span className="ml-auto inline-flex items-center gap-0.5 text-[10.5px] uppercase tracking-wide">
          {open ? 'Hide' : 'How it works'}
          {open ? (
            <ChevronDown className="h-3 w-3" aria-hidden />
          ) : (
            <ChevronRight className="h-3 w-3" aria-hidden />
          )}
        </span>
      </button>
      {open ? (
        <div className="space-y-2.5 border-t border-border/60 px-3 pt-2 text-xs leading-relaxed text-muted-foreground">
          {children}
        </div>
      ) : null}
    </div>
  );
};

export default HowItWorksPanel;
